<template>
  <v-card class="card-wrap">
    <v-card class="pa-1 my-2 elevation-0">
      <!-- eslint-disable-next-line prettier/prettier -->
        <single-line-data-table
          :headers="headers"
          :items="dataList"
          :loading="loading"
          :height="height"
          @click:row="clickRow"
          single-select
          fixed-header
          disable-pagination
          dense 
          hide-default-footer>
      </single-line-data-table>
    </v-card>
  </v-card>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import { api } from '@/api/common/instances';
import bus from '@/utils/bus';
import settingsGrid from '@/components/motlet/settings/grid';
import SingleLineDataTable from '@/components/grid/SingleLineDataTableNeo';
import { WORKPRCS_COLORS } from '@/constants';
import Vue from 'vue';

export default {
  components: {
    SingleLineDataTable,
  },
  props: {
    sstmId: {
      type: String,
      required: true,
    },
    code: {
      type: String,
      required: true,
    },
    eventCode: String,
    selected: {
      type: Array,
      required: true,
    },
    clickRow: {
      type: Function,
      default() {
        return function() {};
      },
    },
    height: {
      type: Number,
      default: 100,
    },
    firstRowSelected: {
      type: Boolean,
      default: false,
    },
    rowSelectedEmitName: String,
    onFetch: {
      type: Function,
      default() {
        return function() {};
      },
    },
    storeName: String,
    instances: Array,
    timestamp: {
      type: Number,
      required: true,
    },
    items: Array,
    // BEGIN::Added.by.KWShin.2024.03.12.
    activeIncrement: {
      type: Boolean,
      default: false,
    },
    // FINISH::Added.by.KWShin.2024.03.12.
  },
  data() {
    return {
      list: [],
      loading: false,
      datetime: null,
    };
  },
  watch: {
    descendants: {
      deep: true,
      handler() {
        if (this.datetime) this.fetchData(this.datetime);
      },
    },
    timestamp: {
      immediate: true,
      handler(value) {
        this.fetchData(value);
      },
    },
  },
  computed: {
    headers() {
      return settingsGrid[this.code].fields.filter(h => this.selected.includes(h.value));
    },
    onEventCode() {
      return this.eventCode || this.code;
    },
    descendants() {
      return this.storeName ? this.$store.getters[`${this.storeName}/get`] : this.instances ?? [];
    },
    dataList: {
      get() {
        // BEGIN::2022.11.30.KWShin.
        // 바로 직전에 읽어왔던 목록이 다음에 읽어온 목록인 것처럼 dataList가 리턴되는 현상이 존재하여
        // 아래 줄을 막고 그냥 this.list를 리턴하였다.
        // props.items를 parent component에서 sync를 하고 있는데 여기에 어떤 영향을 미칠 지 파악하지 못하였다.
        // return this.items ?? this.list;
        return this.list;
        // FINISH::2022.11.30.KWShin.
      },
      set(data) {
        this.$emit('update:items', data);
        this.list = data;
      },
    },
  },
  methods: {
    // BEGIN:: Added.by.KWShin.2022.10.25.
    // 수집된 데이터가 DB에 미처 저장되기 전에 조회가 들어가서
    // 가끔식 Grid에 데이터가 조회되지 않는 문제가 있어서
    // 약간의 delay를 주기 위해 fetchData()에 setTimeout()을 설정하고
    // 기존의 fetchData()를 fetchDataLazy()로 이름을 변경함.
    fetchData(datetime) {
      if (this.activeIncrement) {
        // 실시간 상태가 아니면 굳이 delay를 줄 필요 없다. 괜히 화면 반응만 굼띠다.
        this.fetchDataLazy(datetime);
      } else {
        setTimeout(this.fetchDataLazy, 500, datetime);
      }
    },
    // FINISH:: Added.by.KWShin.2022.10.25.
    async fetchDataLazy(datetime) {
      if (!this.datetime) this.datetime = datetime;
      this.loading = true;
      const params = {
        endYmdhms: datetime,
        sstmId: this.sstmId,
        motletCode: this.code,
      };
      const descendants = (this.descendants || []).filter(desc => desc.active);
      if (descendants.length == 1) params.instId = descendants[0].id;
      const { data: result } = await api.get('/motlet-data/grid', { params });
      this.dataList = result.reduce((acc, item) => acc.concat(item.data), []).map(d => ({ id: uuidv4(), ...d }));
      this.dataList.forEach(row => {
        row.instColor = descendants.find(d => d.id === row.instId)?.color || '';
      });
      // BEGIN::Modified.by.KWShin.2026.03.31.
      // G_ABAP_CODE의 경우에는 WP Type 에 해당하는 필드명이 reqType이다.
      // 그리고 reqType은 0~6의 숫자로 되어 있으므로, 이 숫자를 WP Type에 해당하는 스트링으로 변환하여 주는
      // filter에 매달아 놓은 'wpTypeQueue' 함수를 이용하여 변환한 후 사용하여야 한다.
      // if (this.code === 'G_ABAP_WORK_PROC_TYPE' || this.code === 'G_ABAP_QUEUE') {
      //   this.dataList.forEach(row => {
      //     const def = WORKPRCS_COLORS.find(w => w.type === row.wpType);
      //     row.wpTypeColor = def ? def.colors[0] : '';
      //   });
      // }
      // ------------------------------------------------------------------------------------------
      if (this.code === 'G_ABAP_WORK_PROC_TYPE') {
        this.dataList.forEach(row => {
          const def = WORKPRCS_COLORS.find(w => w.type === row.wpType);
          row.wpTypeColor = def ? def.colors[0] : '';
        });
      }
      if (this.code === 'G_ABAP_QUEUE') {
        this.dataList.forEach(row => {
          const def = WORKPRCS_COLORS.find(w => w.type === Vue.filter('wpTypeQueue')(row.reqType));
          row.wpTypeColor = def ? def.colors[0] : '';
        });
      }
      // FINISH::Modified.by.KWShin.2026.03.31.
      if (this.code === 'G_ABAP_WORK_PROC') {
        this.dataList.forEach(row => {
          const def = WORKPRCS_COLORS.find(w => w.type === row.wpTyp);
          row.wpTypeColor = def ? def.colors[0] : '';
        });
      }
      /*
       * Modifield.by.KWShin2024.03.08.
       * 아웅... onFetch()를 dataList를 읽어오기도 전에 호출하면 어떡허냐...
       * 이 함수의 꼭대기에 있던 것을 dataList 구해온 다음으로 옮겼다.
       * 그랬더니, 다이어그램 즉각적으로 잘 나온다.
       */
      this.onFetch({ eventCode: this.onEventCode, data: this.dataList });

      if (this.firstRowSelected) {
        const [row] = this.dataList;
        (row || {}).isSelected = true;
        if (this.rowSelectedEmitName) bus.$emit(this.rowSelectedEmitName, row);
      }
      this.loading = false;
    },
  },
};
</script>
